package com.gastoncantera.intercom.presence

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.FieldPath
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

/** Tope de valores que admite una consulta `in` de Firestore. */
private const val FIRESTORE_IN_LIMIT = 30

@Serializable
private data class UserPresence(
    // callIds (buzones de depto) que escucha el usuario: lo usa el notificador para resolver a qué
    // usuarios avisar cuando entra una llamada a `calls/{callId}`. Es por-usuario, no por-dispositivo.
    val callIds: List<String> = emptyList(),
    // Tokens FCM del usuario (multi-dispositivo): token -> descripción del dispositivo, para saber a
    // quién pertenece cada uno. El notificador les manda el push de llamada entrante.
    val tokens: Map<String, String> = emptyMap(),
)

/**
 * Presencia mínima para el intercom: al loguear se crea/actualiza un doc en
 * `users/{uid}` (el id del doc ES el UID de Firebase Auth). Que el doc exista == "el usuario se
 * registró alguna vez".
 *
 * Además guarda los [UserPresence.callIds] que escucha y sus [UserPresence.tokens] FCM, para que el
 * notificador sepa a quién avisar.
 *
 * Importante: [register] y [addPushToken] escriben SOLO su campo (merge), nunca el doc entero. Así
 * dos corrutinas en paralelo (una guardando callIds, otra el token) no se pisan entre sí.
 */
class PresenceRepository {
    private val users = Firebase.firestore.collection("users")

    /** Registra/actualiza SOLO los callIds que escucha el usuario, preservando los tokens. */
    suspend fun register(uid: String, callIds: List<String> = emptyList()) {
        // mergeFields("callIds") crea el doc si no existe y deja `tokens` intacto.
        users.document(uid).set(UserPresence(callIds = callIds), "callIds") {
            encodeDefaults = true
        }
    }

    /** Borra el doc del usuario por completo. */
    suspend fun unregister(uid: String) {
        users.document(uid).delete()
    }

    /**
     * Registra el token FCM de ESTE dispositivo. Reemplaza tokens previos con la misma [device]
     * (p. ej. refresh de FCM o reinstalación); conserva los de otros dispositivos.
     */
    suspend fun addPushToken(uid: String, token: String, device: String) {
        val ref = users.document(uid)
        val current = ref.currentOrNull()
        for (oldToken in current?.tokens.orEmpty()) {
            if (oldToken.value == device && oldToken.key != token) {
                ref.update(FieldPath("tokens", oldToken.key) to FieldValue.delete)
            }
        }
        ref.set(mapOf("tokens" to mapOf(token to device)), merge = true)
    }

    /**
     * Quita el token FCM de ESTE dispositivo (logout por-dispositivo): los demás dispositivos del
     * usuario siguen recibiendo notificaciones. Si era el último token —o este dispositivo nunca
     * obtuvo uno y no hay otros tokens— borra el doc completo.
     */
    suspend fun removePushToken(uid: String, token: String?) {
        val ref = users.document(uid)
        val current = ref.currentOrNull() ?: return
        if (token == null) {
            if (current.tokens.isEmpty()) ref.delete()
            return
        }
        if (token !in current.tokens) return
        if (current.tokens.size == 1) {
            ref.delete()
        } else {
            // FieldPath trata el token como segmento literal (evita problemas con `:` u otros chars).
            ref.update(FieldPath("tokens", token) to FieldValue.delete)
        }
    }

    /**
     * De los [uids] pedidos, los que tienen doc de presencia (se registraron alguna vez).
     *
     * Filtra en el server por id de documento, así el portero no se trae la colección `users`
     * completa (que incluye residentes de otros consorcios y le llega un evento por cada cambio de
     * token ajeno). Un listener por cada [FIRESTORE_IN_LIMIT] UIDs, no uno por residente.
     */
    fun registeredUids(uids: List<String>): Flow<Set<String>> {
        val chunks = uids.distinct().chunked(FIRESTORE_IN_LIMIT)
        if (chunks.isEmpty()) return flowOf(emptySet())
        return combine(
            chunks.map { chunk ->
                users.where { FieldPath.documentId inArray chunk }
                    .snapshots
                    .map { snap -> snap.documents.map { it.id }.toSet() }
            },
        ) { sets -> sets.flatMapTo(mutableSetOf()) { it } }
    }

    private suspend fun DocumentReference.currentOrNull(): UserPresence? {
        val snap = get()
        return if (snap.exists) snap.data(UserPresence.serializer()) else null
    }
}
